/* eslint-disable class-methods-use-this */
import ReduceStore from "flux/lib/FluxReduceStore";

import { AITweak, AITweakInteraction } from "../../../foundation/api/model";
import { AITweakInteractionStatus, AITweakSource, AITweakStatus } from "../../../foundation/api/model/AITweak/AITweak";
import { AllPayloads } from "../payloads";

import * as Payloads from "./AITweakActionPayloads";
import AITweakActionTypes from "./AITweakActionTypes";
import { cancelledStateResponse } from "./constants";

interface AITweakData {
    [pid: string]: {
        [airid: string]: AITweak;
    };
}

interface State {
    tweaks: AITweakData;
}

class AITweakDataStore extends ReduceStore<State, AllPayloads> {
    getInitialState(): State {
        return {
            tweaks: {}
        };
    }

    getProjectAITweakSuccess(state: State, {
        pid,
        aiTweak
    }: Payloads.GetProjectAITweakSuccess): State {
        const prevProjectTweaks = state.tweaks[pid] || {};

        return {
            ...state,
            tweaks: {
                ...state.tweaks,
                [pid]: {
                    ...prevProjectTweaks,
                    [aiTweak._id]: aiTweak
                }
            }
        };
    }

    createAITweakInteraction(
        state: State,
        action: Payloads.CreateAITweakInteraction
    ): State {
        const {
            pid, airid, interaction, status
        } = action;
        const previousProjectTweaks = state.tweaks[pid] || {};
        const existingTweak = previousProjectTweaks[airid];

        const createdInteraction: AITweakInteraction = {
            ...interaction,
            _id: "_id" in interaction ? interaction._id : "newTweakInteractionId",
            source: "source" in interaction ? interaction.source : AITweakSource.USER,
            status: "status" in interaction ? interaction.status : AITweakInteractionStatus.PENDING
        };

        const updatedTweak = existingTweak
            ? {
                ...existingTweak,
                interactions: [...existingTweak.interactions, createdInteraction],
                status: status || existingTweak.status
            }
            : {
                _id: airid,
                interactions: [createdInteraction],
                status: status || AITweakStatus.PENDING
            };

        return {
            ...state,
            tweaks: {
                ...state.tweaks,
                [pid]: {
                    ...previousProjectTweaks,
                    [airid]: updatedTweak
                }
            }
        };
    }

    updateAITweakInteraction(
        state: State,
        action: Payloads.UpdateAITweakInteraction
    ): State {
        const {
            pid, airid, interactionId, interaction, status
        } = action;
        const projectTweaks = state.tweaks[pid];
        const tweak = projectTweaks?.[airid];

        if (!tweak) {
            return state;
        }

        const updatedInteractions = tweak.interactions.map(tweakInteraction =>
            (tweakInteraction._id === interactionId
                ? { ...tweakInteraction, status: interaction.status, _id: interaction._id }
                : tweakInteraction)
        );

        return {
            ...state,
            tweaks: {
                ...state.tweaks,
                [pid]: {
                    ...projectTweaks,
                    [airid]: {
                        ...tweak,
                        status: status || tweak.status,
                        interactions: updatedInteractions
                    }
                }
            }
        };
    }

    setAITweakChatRating(
        state: State,
        action: Payloads.SetAITweakChatRating
    ): State {
        const {
            pid, airid, chatLineId, rating
        } = action;
        const projectTweaks = state.tweaks[pid];
        const tweak = projectTweaks?.[airid];

        if (!tweak) {
            return state;
        }

        const updatedInteractions = tweak.interactions.map(tweakInteraction => (
            tweakInteraction._id === chatLineId
                ? { ...tweakInteraction, rating }
                : tweakInteraction
        ));

        return {
            ...state,
            tweaks: {
                ...state.tweaks,
                [pid]: {
                    ...projectTweaks,
                    [airid]: {
                        ...tweak,
                        interactions: [...updatedInteractions]
                    }
                }
            }
        };
    }

    stopAITweak(
        state: State,
        action: Payloads.StopAITweak
    ): State {
        const { pid, airid } = action;
        const projectTweaks = state.tweaks[pid];
        const tweak = projectTweaks?.[airid];

        if (!tweak) {
            return state;
        }

        const updatedInteractions = [
            ...tweak.interactions,
            cancelledStateResponse
        ];

        return {
            ...state,
            tweaks: {
                ...state.tweaks,
                [pid]: {
                    ...projectTweaks,
                    [airid]: {
                        ...tweak,
                        status: AITweakStatus.IDLE,
                        interactions: [...updatedInteractions]
                    }
                }
            }
        };
    }

    reduce(state: State, action: AllPayloads): State {
        switch (action.type) {
            case AITweakActionTypes.GET_PROJECT_AI_TWEAK_SUCCESS:
                return this.getProjectAITweakSuccess(state, action);
            case AITweakActionTypes.CREATE_AI_TWEAK_INTERACTION:
                return this.createAITweakInteraction(state, action);
            case AITweakActionTypes.UPDATE_AI_TWEAK_INTERACTION:
                return this.updateAITweakInteraction(state, action);
            case AITweakActionTypes.SET_AI_TWEAK_CHAT_RATING:
                return this.setAITweakChatRating(state, action);
            case AITweakActionTypes.STOP_AI_TWEAK:
                return this.stopAITweak(state, action);

            default:
                return state;
        }
    }
}

export default AITweakDataStore;
export {
    State as AITweakDataStoreState,
    AITweakData
};
